class SergeShowMore {
  constructor() {
    this.btn = document.getElementById('show-more-btn');
    this.countEl = document.getElementById('show-more-count');
    this.wrapper = document.getElementById('show-more-wrapper');
    this.container = document.getElementById('ProductGridContainer');

    // Abort if no product grid container found on this page
    if (!this.container) return;

    this.isLoading = false;

    // Tracks how many pages have been auto-loaded via scroll; resets on filter/sort change
    this.autoLoadCount = 0;
    this.scrollObserver = null;

    // Set initial count display and button visibility
    this.updateUI();

    if (this.btn) {
      this.btn.addEventListener('click', () => this.loadMore());
    }

    // Start scroll-based auto-loading if enabled
    this.setupScrollObserver();

    // Watch for filter/sort changes that replace the grid
    this.observeContainer();
  }

  // Read pagination state from the JSON script tag inside ProductGridContainer
  getState() {
    const el = document.getElementById('show-more-data');
    if (!el) return null;
    try { return JSON.parse(el.textContent); }
    catch { return null; }
  }

  // Count currently rendered product cards in the grid
  getLoadedCount() {
    const grid = document.getElementById('product-grid');
    return grid ? grid.querySelectorAll('.serge-card-product').length : 0;
  }

  // Disconnect and recreate the IntersectionObserver based on current state/settings.
  // Called on init and after every filter/sort change.
  setupScrollObserver() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
      this.scrollObserver = null;
    }

    const state = this.getState();

    // Skip if infinite scroll is disabled in the theme editor, or the auto-load limit is already reached
    if (!state || !state.infiniteScroll || !this.wrapper) return;
    if (this.autoLoadCount >= (state.infiniteScrollLimit ?? 2)) return;

    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting || this.isLoading) return;

        const s = this.getState();
        const hasMore = s && s.nextUrl && this.getLoadedCount() < s.total;
        const limit = s ? (s.infiniteScrollLimit ?? 2) : 2;

        if (hasMore && this.autoLoadCount < limit) {
          this.loadMore(true);
        }
      },
      // Trigger 300px before the wrapper scrolls into view for a smooth load
      { rootMargin: '300px 0px 0px 0px' }
    );

    this.scrollObserver.observe(this.wrapper);
  }

  // Refresh the count text and show/hide the button based on current state
  updateUI() {
    const state = this.getState();
    const loaded = this.getLoadedCount();
    const hasMore = state && state.nextUrl && loaded < state.total;

    if (this.countEl) {
      if (state && state.total > 0) {
        // Once all products are loaded, use the DOM count as the total rather than
        // paginate.items — the Liquid total includes products hidden via the
        // hide_on_collection metafield, which never render as cards, causing mismatches.
        const displayTotal = hasMore ? state.total : loaded;
        this.countEl.textContent = `Showing ${loaded} of ${displayTotal}`;
      } else {
        this.countEl.textContent = '';
      }
    }

    if (this.btn) {
      const limit = state ? (state.infiniteScrollLimit ?? 2) : 2;
      // Infinite scroll is "active" while it's enabled and hasn't hit its limit yet
      const autoActive = state && state.infiniteScroll && this.autoLoadCount < limit;
      // Only show the button when there are more pages AND auto-loading won't handle it
      const showButton = hasMore && !autoActive;
      this.btn.classList.toggle('hide', !showButton);
    }

    if (this.wrapper) {
      const state2 = this.getState();
      this.wrapper.hidden = !state2 || state2.total === 0;
    }
  }

  // Watch ProductGridContainer for DOM changes triggered by facets.js filter/sort updates
  observeContainer() {
    const observer = new MutationObserver(() => {
      // Re-wire click handler if facets.js swapped in a new button element
      const newBtn = document.getElementById('show-more-btn');
      if (newBtn && newBtn !== this.btn) {
        this.btn = newBtn;
        this.btn.addEventListener('click', () => this.loadMore());
      } else if (!this.btn) {
        this.btn = document.getElementById('show-more-btn');
        if (this.btn) this.btn.addEventListener('click', () => this.loadMore());
      }

      // Filter/sort change: reset auto-load counter and restart scroll detection
      this.autoLoadCount = 0;
      this.updateUI();
      this.setupScrollObserver();
    });

    observer.observe(this.container, { childList: true });
  }

  // Fetch the next page of products and append them to the grid.
  // isAuto=true when triggered by the scroll observer (counts toward the limit).
  async loadMore(isAuto = false) {
    if (this.isLoading) return;
    const state = this.getState();
    if (!state || !state.nextUrl) return;

    this.isLoading = true;
    if (this.btn) {
      this.btn.disabled = true;
      this.btn.classList.add('loading');
    }

    try {
      // Append section_id so Shopify returns only this section's HTML
      const url = new URL(state.nextUrl, window.location.origin);
      url.searchParams.set('section_id', state.sectionId);

      const response = await fetch(url.toString());
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');

      // Extract and append new product cards to the live grid
      const newGrid = parsed.getElementById('product-grid');
      const newCards = newGrid ? newGrid.querySelectorAll('.serge-card-product') : [];
      const liveGrid = document.getElementById('product-grid');
      newCards.forEach((card) => liveGrid.appendChild(card));

      // Update the state JSON with the next page's pagination data.
      // The fetched page's JSON won't include infiniteScroll settings (they're Liquid
      // section settings), so copy them forward from the current state.
      const newStateEl = parsed.getElementById('show-more-data');
      const liveStateEl = document.getElementById('show-more-data');
      if (newStateEl && liveStateEl) {
        const merged = JSON.parse(newStateEl.textContent);
        merged.infiniteScroll = state.infiniteScroll;
        merged.infiniteScrollLimit = state.infiniteScrollLimit;
        liveStateEl.textContent = JSON.stringify(merged);
      } else if (liveStateEl) {
        liveStateEl.textContent = JSON.stringify({ ...state, nextUrl: null });
      }

      // Increment the auto-load counter and stop the observer once the limit is reached
      if (isAuto) {
        this.autoLoadCount++;
        const limit = this.getState()?.infiniteScrollLimit ?? 2;
        if (this.autoLoadCount >= limit) {
          if (this.scrollObserver) {
            this.scrollObserver.disconnect();
            this.scrollObserver = null;
          }
        }
      }

      this.updateUI();
    } catch (err) {
      console.error('Show more failed:', err);
    } finally {
      // Always restore button state, even on error
      this.isLoading = false;
      if (this.btn) {
        this.btn.disabled = false;
        this.btn.classList.remove('loading');
      }
    }
  }
}


document.addEventListener('DOMContentLoaded', () => new SergeShowMore());
